import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { utils, Box, Token, Button, IconCross } from '@screentone/core';

function DropDown({
  dropDownArrow,
  dropDownClassName,
  dropDownItems,
  focusedElementNum,
  focusedElementRef,
  inputRef,
  onInput,
  onSelect,
  setFocusedElementNum,
  tags
}) {
  let cssClasses = ['st_autocomplete__dropdown'];
  cssClasses = utils.classNames.addClassNames(dropDownClassName, cssClasses);
  useEffect(() => {
    if (focusedElementRef.current && focusedElementNum !== -1) {
      focusedElementRef.current.focus();
    }
  });
  return /*#__PURE__*/React.createElement(Box, {
    arrow: dropDownArrow,
    className: cssClasses.join(' '),
    margin: {
      top: 'md'
    }
  }, dropDownItems.map((item, i) => {
    const isReactElem = React.isValidElement(item);
    let newItem;

    if (i === focusedElementNum && isReactElem) {
      /**
       * Since react cant pass refs to function components, we have to resort to the hack of
       * cloning a react element and setting the ref prop manually.
       *
       * *BE CAREFUL NOT TO REMOVE REFERENCES TO OBJECTS IN PROPS*
       */
      newItem = Object.freeze(_extends({}, item, {
        props: _extends({}, item.props, {
          className: (item.props.className || '') + " st_autocomplete__dropdown-item-child",
          ref: focusedElementRef,
          tabIndex: -1
        }),
        ref: focusedElementRef
      }));
    } else if (isReactElem) {
      newItem = React.cloneElement(item, {
        className: (item.props.className || '') + " st_autocomplete__dropdown-item-child",
        tabIndex: -1
      }, item.props.children);
    } else {
      newItem = /*#__PURE__*/React.createElement("div", {
        className: "st_btn st_btn--tertiary st_btn--full-width st_autocomplete__dropdown-btn"
      }, item);
    }

    return /*#__PURE__*/React.createElement("div", {
      className: "st_autocomplete__dropdown-item " + (i === focusedElementNum ? 'st_autocomplete__dropdown-item--active' : ''),
      key: isReactElem ? item.key : item,
      onClickCapture: e => {
        if (isReactElem) {
          if (e.currentTarget.children.length === 1) {
            e.currentTarget.children[0].click();
          }
        } else if (onSelect && !tags.includes(item)) {
          onSelect(tags.concat(item));
        }

        onInput('');
        inputRef.current.focus();
        return setFocusedElementNum(-1); // set focus on input
      },
      onKeyDown: e => {
        /* eslint-disable-line consistent-return */
        if (e.key === 'Enter' || e.key === 'Space') {
          if (!React.isValidElement(dropDownItems[focusedElementNum]) && onSelect && !tags.includes(item)) {
            onSelect(tags.concat(item));
            onInput('');
          }

          inputRef.current.focus();
          return setFocusedElementNum(-1); // set focus on input
        }
      },
      ref: i === focusedElementNum ? focusedElementRef : null // ! !isReactElem && i === ...
      ,
      role: "button",
      tabIndex: -1
    }, newItem);
  }));
}

DropDown.propTypes = {
  /** Position of the arrow within the dropdown */
  dropDownArrow: PropTypes.oneOf(['top', 'left', 'right', 'bottom', 'top-left', 'top-right']).isRequired,

  /** Extra CSS classes for drop-down menu, if any */
  dropDownClassName: utils.classNames.classNameProps,

  /**
   * An array of content that will populate the drop-down, either of type `string` or jsx. If jsx,
   * an `onClick` handler **MUST** be provided for every item that you wish for a user to be able to
   * select. `onFocus` and `onBlur` event handlers will also be accessible from each item.
   * **NOTE: Each item must be enclosed by a single tag, do not use a Fragment.**
   */
  dropDownItems: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired])),

  /**
   * A number representing which element in the drop-down list has focus. Used for arrow key
   * navigation. -1 represents focus on the input.
   */
  focusedElementNum: PropTypes.number.isRequired,

  /** Ref for focused element */
  focusedElementRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: typeof Element === 'undefined' ? PropTypes.any : PropTypes.instanceOf(Element)
  })]),

  /** Ref for input component */
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: typeof Element === 'undefined' ? PropTypes.any : PropTypes.instanceOf(Element)
  })]).isRequired,

  /** A state setting function for updating the value of `inputValue` */
  onInput: PropTypes.func.isRequired,

  /**
   * A function for handling the selection of a new item of type `string`. This function takes in an
   * array representing the new state of `tags`. `dropDownItems` of type `string` will not be able
   * to be selected if this function is not provided.
   */
  onSelect: PropTypes.func,

  /** A state-setting function for updating `focusedElementNum` when focus changes */
  setFocusedElementNum: PropTypes.func.isRequired,

  /**
   * An array of either type `string` or of objects representing a stipple `Token` component's props
   * (`avatar`, `className`, `color`, `icon`, and `value`)
   */
  tags: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.shape({
    avatar: PropTypes.shape({
      avatar: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
      initials: PropTypes.string
    }),
    className: PropTypes.string,
    color: PropTypes.oneOf(['blurple', 'gray', 'rose', 'lava', 'tangerine', 'sunshine', 'emerald', 'ice']),
    icon: PropTypes.any,
    value: PropTypes.string
  }).isRequired])).isRequired
};
DropDown.defaultProps = {
  dropDownClassName: null,
  dropDownItems: [],
  focusedElementRef: null,
  onSelect: null
};

function Input({
  autoCompleteHasFocus,
  disabled,
  error,
  focusedElementNum,
  inputRef,
  inputProps,
  inputValue,
  onDismiss,
  onInput,
  onSubmit,
  placeholder,
  setFocusedElementNum,
  tags
}) {
  const cssClasses = ['st_autocomplete__input'];
  const disabledOrErrorProps = {};

  if (autoCompleteHasFocus) {
    cssClasses.push('st_autocomplete__input----dropdown-open');
  }

  if (tags.length) {
    cssClasses.push('st_autocomplete__input--has-tags');
  }

  if (disabled) {
    disabledOrErrorProps.disabled = disabled;
    cssClasses.push('st_autocomplete__input--disabled');
  }

  if (error) {
    disabledOrErrorProps['aria-invalid'] = error;
    cssClasses.push('st_autocomplete__input--invalid');
  }

  return /*#__PURE__*/React.createElement("input", _extends({
    className: cssClasses.join(' '),
    disabled: disabled,
    onChange: e => {
      if (onInput) onInput(e.target.value);
    },
    onClick: () => {
      if (focusedElementNum !== -1) setFocusedElementNum(-1);
    } // reset focus
    ,
    onKeyDownCapture: e => {
      // to stop cursor from jumping
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
    },
    onKeyDown: e => {
      if (e.key === 'Backspace' && !e.target.value && tags.length) {
        onDismiss(tags.slice(0, tags.length - 1));
      } else if (onSubmit && (e.key === 'Enter' || e.key === 'Space') && e.target.value) {
        if (!tags.includes(e.target.value)) {
          onSubmit(tags.concat(e.target.value.trim()));
        }

        onInput('');
      }
    },
    placeholder: placeholder,
    ref: inputRef,
    value: typeof inputValue === 'string' ? inputValue : undefined
  }, disabledOrErrorProps, inputProps));
}

Input.propTypes = {
  /** Whether the input has focus */
  autoCompleteHasFocus: PropTypes.bool,

  /** Whether input is disabled */
  disabled: PropTypes.bool,

  /** Whether input is invalid, e.g. error state */
  error: PropTypes.bool,

  /**
   * A number representing which element in the drop-down list has focus. Used for arrow key
   * navigation. -1 represents focus on the input.
   */
  focusedElementNum: PropTypes.number.isRequired,

  /** Ref for input component */
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: typeof Element === 'undefined' ? PropTypes.any : PropTypes.instanceOf(Element)
  })]),

  /** An object representing the extra props to be passed directly to the input. */
  inputProps: PropTypes.shape({}).isRequired,

  /** A string representing the value currently held in the input */
  inputValue: PropTypes.string,

  /**
   * A function for handling the clearing/removal of elements from `tags`, takes in an array
   * representing the new state after elements have been removed.
   */
  onDismiss: PropTypes.func.isRequired,

  /** A state setting function for updating the value of `inputValue` */
  onInput: PropTypes.func.isRequired,

  /** A function for handling the selection of a new item via user hitting the `Enter` key in the
   * input field. Takes in an array representing the new state after elements have been removed.
   */
  onSubmit: PropTypes.func,

  /** Placeholder text for the input trigger for the calendar */
  placeholder: PropTypes.string,

  /** A state-setting function for updating `focusedElementNum` when focus changes */
  setFocusedElementNum: PropTypes.func.isRequired,

  /**
   * An array of either type `string` or of objects representing a stipple `Token` component's props
   * (`avatar`, `className`, `color`, `icon`, and `value`)
   */
  tags: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.shape({
    avatar: PropTypes.shape({
      avatar: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
      initials: PropTypes.string
    }),
    className: PropTypes.string,
    color: PropTypes.oneOf(['blurple', 'gray', 'rose', 'lava', 'tangerine', 'sunshine', 'emerald', 'ice']),
    icon: PropTypes.any,
    value: PropTypes.string
  }).isRequired])).isRequired
};
Input.defaultProps = {
  autoCompleteHasFocus: false,
  disabled: false,
  error: false,
  inputRef: null,
  inputValue: null,
  onSubmit: null,
  placeholder: ''
};

function TokenWrapper({
  disabled,
  error,
  onDismiss,
  tags
}) {
  return tags.map((item, i) => {
    const {
      avatar,
      className = '',
      color,
      icon,
      value
    } = item || {};
    const text = value || item;
    return /*#__PURE__*/React.createElement(Token, {
      avatar: avatar,
      className: className,
      color: error ? 'lava' : color,
      icon: icon,
      key: text || i,
      margin: {
        horizontal: 'xs'
      },
      onDismiss: !disabled ? () => onDismiss(tags.filter(itm => {
        if (!itm) return false;
        return typeof item === 'string' ? itm !== text : itm.value !== text;
      })) : null
    }, text);
  });
}

TokenWrapper.propTypes = {
  /** Whether input is disabled */
  disabled: PropTypes.bool,

  /** Whether input is invalid, e.g. error state */
  error: PropTypes.bool,

  /**
   * A function for handling the clearing/removal of elements from `tags`, takes in an array
   * representing the new state after elements have been removed.
   */
  onDismiss: PropTypes.func.isRequired,

  /**
   * An array of either type `string` or of objects representing a stipple `Token` component's props
   * (`avatar`, `className`, `color`, `icon`, and `value`)
   */
  tags: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.shape({
    avatar: PropTypes.shape({
      avatar: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
      initials: PropTypes.string
    }),
    className: PropTypes.string,
    color: PropTypes.oneOf(['blurple', 'gray', 'rose', 'lava', 'tangerine', 'sunshine', 'emerald', 'ice']),
    icon: PropTypes.any,
    value: PropTypes.string
  }).isRequired])).isRequired
};
TokenWrapper.defaultProps = {
  disabled: false,
  error: false
};

/**
 * Given two DOM elements, `autocomplete` and `next`, check to see if `autocomplete` is an ancestor
 * of `next`. If so return `true`, if not return `false`.
 *
 * @param {Element} autocomplete The autocomplete element
 * @param {Element} next The next element to have focus
 * @returns {boolean}
 */

const checkForAncestor = (autocomplete, next) => {
  let current = next;

  while (current && current !== document.body) {
    if (current === autocomplete) return true;
    current = current.parentElement;
  }

  return false;
};
/**
 * Given a list of jsx, `dropDownItems`, a starting index and a direction, traverse the array of jsx
 * in search of the next focusable index. If an element with the prop `data-inactive` is found, skip
 * this index.
 *
 * @param {ReactElement} dropDownItems An array of jsx containing the items that will populate the
 * dropdown
 * @param {number} start A starting index for array traversal
 * @param {1 | -1} direction the direction to traverse the array, forward (1) or backwards (-1)
 * @returns {number}
 */

const findNextIndex = (dropDownItems, start, direction = -1) => {
  let i = start + direction;

  while (i > -1 && i < dropDownItems.length) {
    if (!React.isValidElement(dropDownItems[i])) break; // * item cannot be inactive

    if (!dropDownItems[i].props['data-inactive']) break;
    i += direction;
  }

  return i;
};

const _excluded = ["className", "componentRef", "disabled", "dropDownArrow", "dropDownClassName", "dropDownItems", "error", "inputProps", "inputValue", "margin", "onBlur", "onDismiss", "onInput", "onSelect", "onSubmit", "placeholder", "tags"];

function AutoComplete(_ref) {
  let {
    className,
    componentRef,
    disabled,
    dropDownArrow,
    dropDownClassName,
    dropDownItems,
    error,
    inputProps,
    inputValue,
    margin,
    onBlur: _onBlur,
    onDismiss,
    onInput,
    onSelect,
    onSubmit,
    placeholder,
    tags
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, _excluded);

  let cssClasses = ['st_autocomplete__wrapper'];
  cssClasses = utils.classNames.addClassNames(className, cssClasses);
  cssClasses = utils.spacing.addSpacingClasses({
    margin,
    cssClasses
  });
  const clearButtonClasses = ['st_autocomplete__clear-button'];
  const disabledOrErrorProps = {};

  if (disabled) {
    disabledOrErrorProps.disabled = disabled;
    cssClasses.push('st_autocomplete__wrapper--disabled');
    clearButtonClasses.push('st_autocomplete__clear-button--disabled');
  }

  if (error) {
    disabledOrErrorProps['aria-invalid'] = !!error;
    cssClasses.push('st_autocomplete__wrapper--invalid');
    clearButtonClasses.push('st_autocomplete__clear-button--invalid');
  }

  const inputRef = useRef(null);
  const focusedElementRef = useRef(null);
  const [focusedElementNum, setFocusedElementNum] = useState(-1); // -1 represents focus on input

  const [autoCompleteHasFocus, setAutoCompleteHasFocus] = useState(false);

  const handleInput = str => {
    if (onInput) onInput(str);else if (inputRef.current) inputRef.current.value = str;
  };

  return /*#__PURE__*/React.createElement("div", _extends({
    className: cssClasses.join(' '),
    onBlur: e => {
      if (!checkForAncestor(e.currentTarget, e.relatedTarget)) {
        if (_onBlur) _onBlur(e);
        setAutoCompleteHasFocus(false);
      }
    },
    onFocus: () => setAutoCompleteHasFocus(true),
    onKeyDownCapture: e => {
      /* eslint-disable-line consistent-return */
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); // prevent page scroll

      if ((e.key === 'Enter' || e.key === 'Space') && React.isValidElement(dropDownItems[focusedElementNum])) {
        focusedElementRef.current.click();
        inputRef.current.focus();
        return setFocusedElementNum(-1);
      } // move focus up the drop-down


      if (e.key === 'ArrowUp') {
        const nextIndex = findNextIndex(dropDownItems, focusedElementNum, -1);

        if (nextIndex === -2) {
          return setFocusedElementNum(findNextIndex(dropDownItems, dropDownItems.length, -1));
        }

        if (nextIndex === -1) {
          inputRef.current.focus();
        }

        return setFocusedElementNum(nextIndex);
      } // move focus down the drop-down


      if (e.key === 'ArrowDown') {
        const nextIndex = findNextIndex(dropDownItems, focusedElementNum, 1);

        if (nextIndex === dropDownItems.length) {
          inputRef.current.focus();
          return setFocusedElementNum(-1);
        }

        return setFocusedElementNum(nextIndex);
      } // return focus to input for any printable character


      if (e.keyCode === 16 // Shift
      || e.keyCode > 47 && e.keyCode < 58 // 0-9
      || e.keyCode > 64 && e.keyCode < 91 // a-z
      || e.keyCode > 105 && e.keyCode < 112 // *+./ (using keypad)
      || e.keyCode > 185 && e.keyCode < 193 // ;=,-./`
      || e.keyCode > 218 && e.keyCode < 223 // [\]'
      ) {
          inputRef.current.focus();
          return setFocusedElementNum(-1);
        }
    },
    ref: componentRef
  }, disabledOrErrorProps, otherProps), /*#__PURE__*/React.createElement("div", {
    className: "st_autocomplete__content--wrapper"
  }, /*#__PURE__*/React.createElement("div", {
    className: "st_autocomplete__token--wrapper"
  }, /*#__PURE__*/React.createElement(TokenWrapper, {
    disabled: disabled,
    error: error,
    onDismiss: onDismiss,
    tags: tags || []
  }), /*#__PURE__*/React.createElement("div", {
    className: "st_autocomplete__input--wrapper"
  }, /*#__PURE__*/React.createElement(Input, {
    autoCompleteHasFocus: autoCompleteHasFocus,
    disabled: disabled,
    error: error,
    focusedElementNum: focusedElementNum,
    inputProps: inputProps,
    inputRef: inputRef,
    inputValue: inputValue,
    onDismiss: onDismiss,
    onInput: handleInput,
    onSubmit: onSubmit,
    placeholder: placeholder,
    setFocusedElementNum: setFocusedElementNum,
    tags: tags || []
  }), autoCompleteHasFocus && dropDownItems.length && !disabled ? /*#__PURE__*/React.createElement(DropDown, {
    dropDownArrow: dropDownArrow,
    dropDownClassName: dropDownClassName,
    dropDownItems: dropDownItems,
    focusedElementNum: focusedElementNum,
    focusedElementRef: focusedElementRef,
    inputRef: inputRef,
    onInput: handleInput,
    onSelect: onSelect,
    tags: tags || [],
    setFocusedElementNum: setFocusedElementNum
  }) : '')), tags && tags.length && !disabled ? /*#__PURE__*/React.createElement(Button, {
    className: clearButtonClasses.join(' '),
    icon: IconCross,
    onClick: () => {
      onDismiss([]);
      setAutoCompleteHasFocus(false);
    }
  }) : ''));
}

AutoComplete.propTypes = {
  /**
   * A function for handling the clearing/removal of elements from `tags`, takes in an array
   * representing the new state after elements have been removed.
   */
  onDismiss: PropTypes.func.isRequired,

  /**
   * An array of either type `string` or of objects representing a stipple `Token` component's props
   * (`avatar`, `className`, `color`, `icon`, and `value`)
   */
  tags: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.shape({
    avatar: PropTypes.shape({
      avatar: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
      initials: PropTypes.string
    }),
    className: PropTypes.string,
    color: PropTypes.oneOf(['blurple', 'gray', 'rose', 'lava', 'tangerine', 'sunshine', 'emerald', 'ice']),
    icon: PropTypes.any,
    value: PropTypes.string
  }).isRequired])).isRequired,

  /** Extra CSS classes, if any */
  className: utils.classNames.classNameProps,

  /** Ref for element */
  componentRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: typeof Element === 'undefined' ? PropTypes.any : PropTypes.instanceOf(Element)
  })]),

  /** Whether input is disabled */
  disabled: PropTypes.bool,

  /** Position of the arrow within the dropdown */
  dropDownArrow: PropTypes.oneOf(['top', 'left', 'right', 'bottom', 'top-left', 'top-right']),

  /** Extra CSS classes for drop-down menu, if any */
  dropDownClassName: utils.classNames.classNameProps,

  /**
   * An array of content that will populate the drop-down, either of type `string` or jsx. If jsx,
   * an `onClick` handler **MUST** be provided for every item that you wish for a user to be able to
   * select. `onFocus` and `onBlur` event handlers will also be accessible from each item.
   * **NOTE: Each item must be enclosed by a single tag, do not use a Fragment.**
   */
  dropDownItems: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired])),

  /** Whether input is invalid, e.g. error state */
  error: PropTypes.bool,

  /** An object representing the extra props to be passed directly to the input. */
  inputProps: PropTypes.shape({}),

  /** The value of the input field  */
  inputValue: PropTypes.string,

  /** Left/top/right/bottom, and also size */
  margin: utils.spacing.marginProps,

  /** A function for handling blur events on the autocomplete component itself. */
  onBlur: PropTypes.func,

  /** A state setting function for updating the value of `inputValue` */
  onInput: PropTypes.func,

  /**
   * A function for handling the selection of a new item of type `string`. This function takes in an
   * array representing the new state of `tags`. `dropDownItems` of type `string` will not be able
   * to be selected if this function is not provided.
   */
  onSelect: PropTypes.func,

  /** A function for handling the selection of a new item via user hitting the `Enter` key in the
   * input field. Takes in an array representing the new state after elements have been removed.
   */
  onSubmit: PropTypes.func,

  /** Placeholder text for the input trigger for the calendar */
  placeholder: PropTypes.string
};
AutoComplete.defaultProps = {
  className: null,
  componentRef: null,
  disabled: false,
  dropDownArrow: 'top',
  dropDownClassName: null,
  dropDownItems: [],
  error: false,
  inputProps: {},
  inputValue: null,
  margin: null,
  onBlur: null,
  onInput: null,
  onSelect: null,
  onSubmit: null,
  placeholder: ''
};

export { AutoComplete as default };
